import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiMail, FiBell, FiUser } from "react-icons/fi";
import { useUser } from "../context/UserContext";
import { permissionService } from "../services/permissionService";
import { API_BASE, api } from "../utils/api";
import {
  applyThemeToDocument,
  getStoredTheme,
  THEME_STORAGE_KEY,
} from "../utils/theme";
import {
  PLAN_STATE_UPDATED_EVENT,
  getPlanSnapshotFromPlanState,
  persistPlanSnapshot,
} from "../utils/planSync";
import { buildContactChangePlanUrl } from "../utils/contactNavigation";

// ASSETS
import Searchimg from "../assets/search.png";
import Micimg from "../assets/mic.png";
import Lightimg from "../assets/light.png";
import Darkimg from "../assets/dark.png";
import setimg from "../assets/settings.png";
import nyafaz from "../assets/nyafazlogo.png";
import mailimg from "../assets/mail.png";


const formatNotificationTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Header = ({ showLogo = false }) => {
  const { user, loading } = useUser();
  const [open, setOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [planStatus, setPlanStatus] = useState(
    String(localStorage.getItem("plan_status") || "active").toLowerCase()
  );
  const [displayPlanLabel, setDisplayPlanLabel] = useState(
    permissionService.getDisplayPlanLabel?.() || "Free Plan"
  );
  const [theme, setTheme] = useState(() => getStoredTheme());
  const [walletBalance, setWalletBalance] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const notificationRef = useRef(null);

  const today = new Date().toLocaleDateString("en-GB");

  const handleLogout = async () => {
    const userId = localStorage.getItem("userId");
    try {
      if (userId) {
        await api("/logout", {
          method: "POST",
          body: JSON.stringify({ userId }),
        });
      }
    } catch {
      /* ignore */
    }
    localStorage.removeItem("userId");
    localStorage.removeItem("orgId");
    localStorage.removeItem("accessModules");
    localStorage.removeItem("role");
    localStorage.removeItem("portalTenantView");
    localStorage.removeItem("portalTenantOrgId");
    localStorage.removeItem("plan");
    localStorage.removeItem("plan_status");
    navigate("/nyafaz-brand");
  };

  const applyTheme = (nextTheme) => {
    const resolvedTheme = applyThemeToDocument(nextTheme);
    setTheme(resolvedTheme);
    localStorage.setItem(THEME_STORAGE_KEY, resolvedTheme);
    window.dispatchEvent(new CustomEvent("themechange", { detail: resolvedTheme }));
  };

  const enableLightMode = () => applyTheme("light");
  const enableDarkMode = () => applyTheme("dark");

  const updateHeaderPlanLabel = (nextPlanState) => {
    const snapshot = getPlanSnapshotFromPlanState(nextPlanState);
    if (!snapshot) return;

    persistPlanSnapshot(snapshot);
    setPlanStatus(snapshot.planStatus || "active");
    setDisplayPlanLabel(snapshot.displayPlanLabel);
  };

  useEffect(() => {
    // initialize wallet balance from localStorage so header shows value even without auth
    try {
      const stored = localStorage.getItem("wallet_balance");
      if (stored && walletBalance === null) {
        const parsed = parseFloat(stored);
        if (!Number.isNaN(parsed)) setWalletBalance(parsed);
      }
    } catch (e) {
      // ignore
    }

    const token = localStorage.getItem("userId");
    if (!token) return;

    let timer;
    const fetchNotifications = async ({ silent = false } = {}) => {
      if (!silent) {
        setNotificationsLoading(true);
      }
      try {
        const [notificationsRes, planStateRes] = await Promise.all([
          fetch(`${API_BASE}/notifications`, {
            credentials: 'include',
            headers: { },
          }),
          fetch(`${API_BASE}/subscriptions/plan-state`, {
            credentials: 'include',
            headers: { },
          }),
        ]);

        // Attempt to fetch wallet/billing summary for quick display in header
        try {
          const walletRes = await fetch(`${API_BASE}/billing/wallet?sync_consumption=0`, {
            credentials: 'include',
            headers: { } });
          if (walletRes.ok) {
            const w = await walletRes.json();
            const bal = w?.summary_insights?.runtime_current_balance;
            if (typeof bal === 'number') {
              setWalletBalance(bal);
              try { localStorage.setItem('wallet_balance', String(bal)); } catch {}
            }
          } else {
            // fallback to localStorage value if available
            const stored = localStorage.getItem('wallet_balance');
            if (stored) setWalletBalance(parseFloat(stored));
          }
        } catch {
          const stored = localStorage.getItem('wallet_balance');
          if (stored) setWalletBalance(parseFloat(stored));
        }

        const data = await notificationsRes.json();
        setNotifications(data?.notifications || []);
        setUnreadCount(Number(data?.unread_count || 0));

        if (planStateRes.ok) {
          const planStateData = await planStateRes.json();
          const nextPlanState = planStateData?.data || null;
          updateHeaderPlanLabel(nextPlanState);
        }
      } catch {
        setNotifications([]);
        setUnreadCount(0);
      } finally {
        if (!silent) {
          setNotificationsLoading(false);
        }
      }
    };

    fetchNotifications({ silent: notificationsOpen });

    if (!notificationsOpen) {
      timer = setInterval(() => {
        fetchNotifications({ silent: true });
      }, 10000);
    }

    return () => clearInterval(timer);
  }, [location.pathname, notificationsOpen]);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  useEffect(() => {
    const syncTheme = () => {
      const nextTheme = getStoredTheme();
      setTheme((currentTheme) => (currentTheme === nextTheme ? currentTheme : nextTheme));
      applyThemeToDocument(nextTheme);
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("themechange", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("themechange", syncTheme);
    };
  }, []);

  useEffect(() => {
    const handlePlanStateUpdate = (event) => {
      const nextSnapshot = event?.detail;
      if (nextSnapshot?.displayPlanLabel) {
        setDisplayPlanLabel(nextSnapshot.displayPlanLabel);
      }
      if (nextSnapshot?.planStatus) {
        setPlanStatus(String(nextSnapshot.planStatus).toLowerCase());
      }
    };

    window.addEventListener(PLAN_STATE_UPDATED_EVENT, handlePlanStateUpdate);
    return () => {
      window.removeEventListener(PLAN_STATE_UPDATED_EVENT, handlePlanStateUpdate);
    };
  }, []);

  // Close notification when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [notificationsOpen]);

  const markNotificationRead = async (notificationId) => {
    const token = localStorage.getItem("userId");
    if (!token) return;

    try {
      await fetch(`${API_BASE}/notifications/read`, {
            credentials: 'include',
            method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(notificationId ? { notification_id: notificationId } : {}),
      });

      setNotifications((prev) => prev.map((item) => (
        !notificationId || item.id === notificationId
          ? { ...item, is_read: true }
          : item
      )));
      if (!notificationId) {
        setUnreadCount(0);
      } else {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch {
      // Keep existing UI state if read update fails.
    }
  };

  const notificationTone = (type) => {
    if (type === "warning") return "border-amber-200/60 bg-amber-50/10";
    if (type === "success") return "border-emerald-200/60 bg-emerald-50/10";
    return "border-white/15 bg-white/5";
  };

  if (loading) {
    return <div className="p-4 text-white">Loading...</div>;
  }

  return (
    <div className="header-shell relative z-50 w-full min-w-0 pt-3 md:pt-3.5 lg:pt-4">
      <div className="header-shell__inner relative flex min-h-[4.25rem] w-full flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/10 px-3 py-2 backdrop-blur-xl md:min-h-[4.5rem] md:px-4 lg:h-20 lg:flex-nowrap lg:gap-4 lg:px-6 lg:py-0 lg:pr-10 before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_28px),radial-gradient(circle_at_bottom_left,transparent_16px,black_28px)]">
        <div className="header-shell__left flex min-w-0 flex-1 items-center gap-3 lg:w-[35%] lg:flex-none">
          {showLogo ? (
            <div className="pl-1 lg:pl-4">
              <img src={nyafaz} alt="Nyafaz Logo" className="h-8 object-contain md:h-9 lg:h-10" />
            </div>
          ) : null}
        </div>

        <div className="header-shell__actions flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:gap-3 md:w-auto md:flex-nowrap lg:gap-6">
          {!showLogo && (
            <div className="header-shell__theme-toggle relative flex items-center overflow-hidden rounded-xl bg-white/5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] backdrop-blur-3xl before:pointer-events-none before:absolute before:inset-0 before:rounded-xl before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)]">
              <button
                type="button"
                onClick={enableLightMode}
                aria-pressed={theme === "light"}
                className={`relative z-10 flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold transition-all md:px-3 lg:px-4 ${
                  theme === "light"
                    ? "bg-white/20 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]"
                    : "bg-transparent text-white/70 hover:bg-white/5"
                }`}
              >
                <img src={Lightimg} className="w-3.5 h-3.5 brightness-200" alt="" />
                <span className="header-shell__theme-label hidden lg:inline">Light</span>
              </button>
              <div className="relative z-10 h-6 w-px bg-white/10"></div>
              <button
                type="button"
                onClick={enableDarkMode}
                aria-pressed={theme === "dark"}
                className={`relative z-10 flex items-center gap-2 px-2.5 py-1.5 text-xs font-semibold transition-all md:px-3 lg:px-4 ${
                  theme === "dark"
                    ? "bg-white/20 text-white shadow-[inset_0_1px_1px_rgba(255,255,255,0.4)]"
                    : "bg-transparent text-white/70 hover:bg-white/5"
                }`}
              >
                <span className="header-shell__theme-label hidden lg:inline">Dark</span>
                <img src={Darkimg} alt="" className="header-icon-img w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex min-w-0 flex-wrap items-center justify-end gap-2 md:gap-3 lg:gap-4">
            <div className="relative" ref={notificationRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen((prev) => !prev);
                  setOpen(false);
                }}
                className="header-shell__icon-btn relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white/5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] backdrop-blur-3xl transition before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)] hover:bg-white/10"
                title="Notifications"
              >
                <FiBell className="header-icon h-5 w-5" aria-hidden />
                {unreadCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-white/60 bg-red-500 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 top-12 z-[9999] max-h-[420px] w-[min(360px,calc(100vw-2rem))] overflow-hidden rounded-2xl bg-[#0a6b78]/92 text-white shadow-2xl backdrop-blur-3xl before:pointer-events-none before:absolute before:inset-0 before:rounded-2xl before:border before:border-white/30 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)]">
                  <div className="relative z-10 flex items-center justify-between border-b border-white/10 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">Notifications</p>
                      <p className="text-xs text-white/70">{unreadCount} unread</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => markNotificationRead(null)}
                      className="text-xs font-medium text-white/80 hover:text-white disabled:opacity-50"
                      disabled={!unreadCount}
                    >
                      Mark all read
                    </button>
                  </div>

                  <div className="relative z-10 max-h-[340px] space-y-2 overflow-y-auto px-3 py-3">
                    {notificationsLoading ? (
                      <div className="px-3 py-6 text-center text-sm text-white/75">Loading notifications...</div>
                    ) : notifications.length ? (
                      notifications.map((item) => (
                        <div
                          key={item.id}
                          className={`w-full rounded-xl border px-3 py-3 transition ${notificationTone(item.type)} ${item.is_read ? "opacity-75" : "opacity-100"}`}
                        >
                          <button
                            type="button"
                            onClick={() => !item.is_read && markNotificationRead(item.id)}
                            className="w-full text-left"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-white">{item.title}</p>
                                <p className="mt-1 text-xs leading-5 text-white/75">{item.message}</p>
                              </div>
                              {!item.is_read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-400" />}
                            </div>
                            <p className="mt-2 text-[11px] text-white/55">{formatNotificationTime(item.created_at)}</p>
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-6 text-center text-sm text-white/75">No notifications yet.</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="header-shell__plan-badge relative flex max-w-full flex-wrap items-center gap-2 overflow-hidden rounded-full bg-white/10 px-2.5 py-1.5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.35)] backdrop-blur-3xl md:gap-3 md:px-3 before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_12px,black_20px),radial-gradient(circle_at_bottom_left,transparent_12px,black_20px)]">
              <button
                type="button"
                onClick={() => navigate("/nyafaz-brand/account-billing?tab=credits")}
                className="relative z-10 flex shrink-0 items-center gap-1.5 transition-opacity hover:opacity-90"
                title="Open credits"
              >
                <span className="text-xs text-white/80">₹</span>
                <span className="text-sm font-semibold text-white" data-testid="header-wallet">
                  {walletBalance !== null
                    ? new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(walletBalance)
                    : "—"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => navigate("/nyafaz-brand/account-billing?tab=plan")}
                className="header-shell__plan-label relative z-10 max-w-[7rem] truncate whitespace-nowrap text-[10px] font-semibold text-white transition-opacity hover:opacity-90 sm:max-w-[9rem] md:text-[11px]"
                title="Open plan"
              >
                {displayPlanLabel}
              </button>
            </div>
          </div>

          <div className="header-shell__divider mx-1 hidden h-8 w-px bg-white/50 sm:mx-2 lg:block" />

          <div
            className="header-shell__profile relative flex min-w-0 cursor-pointer items-center gap-2 md:gap-3"
            onClick={() => {
              setOpen(!open);
              setNotificationsOpen(false);
            }}
          >
            <div className="relative group">
              {(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo) ? (
                <div className="relative h-11 w-11 rounded-full bg-white/5 p-0.5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] backdrop-blur-3xl before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)]">
                  <img
                    src={(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo)?.startsWith("http") ? (user?.org_logo || user?.data?.org_logo || user?.user?.org_logo) : `${API_BASE}/${(user?.org_logo || user?.data?.org_logo || user?.user?.org_logo)?.replace(/^\//, "")}`}
                    alt="Profile"
                    className="h-full w-full rounded-full object-contain"
                  />
                </div>
              ) : (
                <div className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white/5 shadow-[inset_0_1px_2px_rgba(255,255,255,0.4)] backdrop-blur-3xl before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:border before:border-white/50 before:content-[''] before:[mask-composite:intersect] before:[-webkit-mask-composite:source-in] before:[mask-image:radial-gradient(circle_at_top_right,transparent_16px,black_24px),radial-gradient(circle_at_bottom_left,transparent_16px,black_24px)]">
                  <FiUser className="header-icon" size={22} />
                </div>
              )}
            </div>
            <div className="header-shell__user-meta hidden text-right sm:block">
              <p className="max-w-[9rem] truncate text-sm font-semibold md:max-w-[12rem]">
                {user?.fullName || user?.data?.fullName || user?.user?.fullName || user?.full_name || "Admin"}
              </p>
              <p className="header-shell__user-date text-xs text-white">{today}</p>
            </div>

            {open && (
              <div className="header-profile-dropdown absolute right-0 top-14 z-[9999] w-48 overflow-hidden rounded-xl">
                <button
                  onClick={() => navigate("/nyafaz-brand/edit-profile")}
                  className="header-profile-dropdown__item"
                >
                  Edit Profile
                </button>

                {!permissionService.isPlatformAdmin() ? (
                  <>
                    <button
                      onClick={() => navigate("/nyafaz-brand/account-billing")}
                      className="header-profile-dropdown__item"
                    >
                      Account &amp; billing
                    </button>
                    <button
                      onClick={() => navigate(buildContactChangePlanUrl())}
                      className="header-profile-dropdown__item"
                    >
                      Change plan
                    </button>
                  </>
                ) : null}

                <button
                  onClick={handleLogout}
                  className="header-profile-dropdown__item"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
